/**
 * La surveillance des délais : ce qui menace le calendrier d'un chantier.
 *
 * Le patron, le 13 septembre : *« il faudrait une card d'alerte sur
 * l'avancement des chantiers : par exemple "si cette phase x n'est pas finie
 * dans 2 jours, nous aurons du retard" ou encore "Date de fin de chantier
 * dépassée" »*.
 *
 * Le type d'alerte `project_delay` existait depuis la première migration, et
 * rien ne l'a jamais posé. Ce fichier ne le pose pas davantage, et c'est un
 * choix : une alerte écrite la nuit en base décrit le chantier tel qu'il était
 * à 3 heures, et reste affichée après qu'on a déclaré la phase finie à 8 h.
 * Un retard se **calcule** à l'ouverture de l'écran, à partir des dates et de
 * l'avancement que l'app tient déjà — rien à écarter, rien qui périme.
 *
 * La règle vit ici en un seul exemplaire : le tableau de bord la lit pour tous
 * les chantiers, la fiche chantier pour le sien. Tout le fichier est pur.
 *
 * Ce qu'il ne dit pas : il ne devine rien d'un chantier dont les phases ne
 * sont pas datées. `unwatchedProjects` compte ceux-là, pour que l'écran dise
 * pourquoi il se tait plutôt que de laisser croire que tout va bien.
 */
import type { AlertSeverity } from "../alerts/services.js";
import { daysUntil } from "../deadlines/types.js";
import { OPEN_PROJECT_STATUSES, orderPhases, phaseLabel } from "./types.js";
import type { ProjectLot, ProjectPhase, ProjectStatus, ProjectSummary } from "./types.js";
import type { MaterialOrderStatus } from "../purchaseOrders/types.js";

export type SiteAlertKind =
  /** Le chantier n'est pas fini et sa date de fin est passée. */
  | "end_overdue"
  /** Une phase est déclarée bloquée. */
  | "phase_blocked"
  /** Une phase non finie a dépassé sa date de fin. */
  | "phase_overdue"
  /** Une phase non finie doit l'être d'ici `PHASE_DUE_SOON_DAYS` jours. */
  | "phase_due_soon"
  /** Une phase aurait dû démarrer et n'en est nulle part. */
  | "phase_not_started"
  /** Le chantier est encore « prévu » après sa date de début. */
  | "start_overdue"
  /** Le chantier est à l'arrêt — subi, sans date de reprise. */
  | "site_stopped"
  /** Le permis n'est pas obtenu et le début approche, ou est passé. */
  | "permit_pending"
  /** Plus de délai consommé que de travaux faits, au-delà de la tolérance. */
  | "behind_schedule"
  /** Du matériel attendu bientôt, et pas encore commandé. */
  | "order_needed"
  /** Toutes les phases sont finies, le chantier est toujours ouvert. */
  | "works_finished"
  /** Le chantier est terminé et rien n'a été facturé. */
  | "nothing_invoiced";

export interface SiteAlert {
  /** Stable d'un rendu à l'autre : la nature et la ligne concernée. */
  key: string;
  kind: SiteAlertKind;
  severity: AlertSeverity;
  projectId: string;
  projectName: string;
  /** La phrase courte, lisible seule. */
  title: string;
  /** Ce qu'on sait de plus : la date, l'écart, l'avancement. */
  detail: string;
  /** Vers où aller pour agir. */
  href: string;
  /** La date en jeu, pour trier le plus ancien d'abord. */
  date: string | null;
}

/** Le chantier tel que la surveillance le lit. */
export type WatchedProject = Pick<
  ProjectSummary,
  "id" | "name" | "status" | "plannedStartDate" | "plannedEndDate" | "health"
> &
  Partial<Pick<ProjectSummary, "statusReason" | "statusChangedAt">> & {
    phases: readonly ProjectPhase[];
    /** Les lots du chantier : ils ordonnent les phases et les nomment. */
    lots?: readonly ProjectLot[];
  };

/** Une commande de matériel, réduite à ce qui dit si elle est en retard. */
export interface WatchedOrder {
  id: string;
  projectId: string | null;
  /** Typé : un renommage de statut casse la compilation au lieu de faire
   *  taire silencieusement l'alerte « matériel à commander ». */
  status: MaterialOrderStatus;
  neededBy: string | null;
}

/**
 * « Si cette phase n'est pas finie dans 2 jours » : le délai du patron, mot
 * pour mot. Plus long, l'alerte arrive quand il n'y a encore rien à faire ;
 * plus court, quand il n'y a plus rien à faire.
 */
export const PHASE_DUE_SOON_DAYS = 2;

/**
 * Écart toléré entre délai écoulé et travaux faits, en points.
 *
 * L'avancement est déclaré à la main, par paliers : un chantier réel n'avance
 * pas en ligne droite. Sous 20 points, l'écart est du bruit de saisie ; au-delà,
 * c'est un chantier qui glisse — ou un avancement que personne ne met à jour,
 * ce qui mérite d'être su aussi.
 */
export const SCHEDULE_TOLERANCE_POINTS = 20;

/** Un matériel se commande quelques jours avant d'être posé. */
export const ORDER_LEAD_DAYS = 3;

/** Un permis se relance deux semaines avant le démarrage, pas la veille. */
export const PERMIT_HORIZON_DAYS = 14;

/**
 * Deux dates ISO se comparent caractere par caractere : `localeCompare` y
 * applique une collation inutile. Sans date en dernier, dans les deux sens.
 */
const compareIso = (a: string | null, b: string | null): number =>
  a === b ? 0 : a == null ? 1 : b == null ? -1 : a < b ? -1 : 1;

/** Les chantiers dont on surveille le calendrier. */
const OUVERTS = new Set<ProjectStatus>(OPEN_PROJECT_STATUSES);

/** Une commande pas encore passée. */
const A_COMMANDER = new Set<MaterialOrderStatus>(["anticipated", "to_order"]);

const RANG: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };

const dateCourte = (iso: string) =>
  new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`)),
  );

/** « aujourd'hui », « demain », « dans 5 jours ». */
function echeance(jours: number): string {
  if (jours <= 0) return "aujourd’hui";
  if (jours === 1) return "demain";
  return `dans ${jours} jours`;
}

// Espaces insécables : sur un téléphone, « % » ou « j » ne partent pas seuls à la ligne.
const pourcent = (n: number) => `${Math.round(n)} %`;

/**
 * Clôt une phrase. « 17 sept. » porte déjà son point : en ajouter un écrirait
 * « démarre le 17 sept.. », ce que le premier test a attrapé.
 */
const phrase = (s: string) => (s.endsWith(".") ? s : `${s}.`);

/**
 * Tout ce qui menace le calendrier des chantiers donnés, le plus grave d'abord.
 *
 * **Une phase ne produit qu'une alerte**, la plus grave : bloquée, sinon en
 * retard, sinon bientôt due, sinon pas démarrée. Une phase bloquée et en
 * retard dite deux fois ferait deux lignes pour un seul coup de téléphone.
 *
 * Les phases ne sont surveillées que sur un chantier **en cours**. Sur un
 * chantier prévu, en attente de permis ou en pause, chacune de ses phases
 * datées serait « en retard » par construction — l'alerte du chantier dit déjà
 * la seule chose utile.
 */
export function siteAlerts(
  projects: readonly WatchedProject[],
  orders: readonly WatchedOrder[],
  today: string,
): SiteAlert[] {
  const alertes: SiteAlert[] = [];

  for (const p of projects) {
    const base = { projectId: p.id, projectName: p.name, href: `/chantiers/${p.id}` };

    if (p.status === "completed") {
      // Seulement quand rien n'est émis : un acompte facturé ne dit pas si le
      // solde l'est, et une alerte qui se trompe sur un chantier soldé serait
      // la première qu'on apprend à ignorer.
      if (p.health.invoicedHt == null) {
        alertes.push({
          ...base,
          key: `nothing_invoiced:${p.id}`,
          kind: "nothing_invoiced",
          severity: "warning",
          title: "Terminé, rien de facturé",
          detail: "Aucune facture émise sur ce chantier.",
          date: p.plannedEndDate,
        });
      }
      continue;
    }
    if (!OUVERTS.has(p.status)) continue;

    // Un arrêt se signale tant qu'il dure, une pause non : la pause est
    // voulue et on sait quand on reprend, l'arrêt est subi et c'est au patron
    // de relancer — client, assureur, expert, fournisseur. La date de fin
    // dépassée continue de s'afficher en dessous : l'arrêt ne l'efface pas.
    if (p.status === "stopped") {
      const depuis = p.statusChangedAt ? p.statusChangedAt.slice(0, 10) : null;
      // `Math.max(0, …)` : une date de changement posterieure a aujourd'hui
      // (horloge decalee, saisie erronee) rendait un nombre negatif, et la
      // phrase disait « Depuis aujourd'hui » pour un arret annonce demain.
      const jours = depuis ? Math.max(0, -daysUntil(depuis, today)) : null;
      alertes.push({
        ...base,
        key: `site_stopped:${p.id}`,
        kind: "site_stopped",
        severity: "warning",
        title: "Chantier à l’arrêt",
        detail: phrase(
          [
            depuis
              ? jours != null && jours > 0
                ? `Depuis le ${dateCourte(depuis)}, ${jours} j`
                : "Depuis aujourd’hui"
              : null,
            p.statusReason ?? "motif non renseigné",
          ]
            .filter(Boolean)
            .join(" · "),
        ),
        date: depuis,
      });
    }

    const phases = orderPhases(p.phases, p.lots);
    const nom = (ph: ProjectPhase) => phaseLabel(ph, p.lots);

    // Les travaux sont faits : ce n'est plus un retard, c'est un chantier à
    // fermer. Dire « date de fin dépassée » ici ferait courir après une équipe
    // qui a fini.
    if (phases.length > 0 && phases.every((ph) => ph.status === "completed")) {
      alertes.push({
        ...base,
        key: `works_finished:${p.id}`,
        kind: "works_finished",
        severity: "info",
        title: "Toutes les phases sont finies",
        detail: "Passez le chantier en « terminé » et facturez le solde.",
        date: p.plannedEndDate,
      });
      continue;
    }

    const avancement = p.health.progressPercent;
    let finDepassee = false;

    if (p.plannedEndDate && p.plannedEndDate < today) {
      finDepassee = true;
      const retard = -daysUntil(p.plannedEndDate, today);
      alertes.push({
        ...base,
        key: `end_overdue:${p.id}`,
        kind: "end_overdue",
        severity: "critical",
        title: "Date de fin dépassée",
        detail: phrase(
          `Fin prévue le ${dateCourte(p.plannedEndDate)}, ${retard} j de retard` +
            (avancement != null ? ` · travaux à ${pourcent(avancement)}` : ""),
        ),
        date: p.plannedEndDate,
      });
    }

    if (p.status === "planned" && p.plannedStartDate && p.plannedStartDate < today) {
      alertes.push({
        ...base,
        key: `start_overdue:${p.id}`,
        kind: "start_overdue",
        severity: "warning",
        title: "Chantier pas encore démarré",
        detail: `Début prévu le ${dateCourte(p.plannedStartDate)} : passez-le en cours, ou décalez sa date.`,
        date: p.plannedStartDate,
      });
    }

    if (p.status === "permit_pending" && p.plannedStartDate) {
      const jours = daysUntil(p.plannedStartDate, today);
      if (jours <= PERMIT_HORIZON_DAYS) {
        alertes.push({
          ...base,
          key: `permit_pending:${p.id}`,
          kind: "permit_pending",
          severity: jours < 0 ? "critical" : "warning",
          title: "Permis toujours en attente",
          detail:
            jours < 0
              ? phrase(`Le chantier devait démarrer le ${dateCourte(p.plannedStartDate)}`)
              : `Début prévu le ${dateCourte(p.plannedStartDate)}, ${echeance(jours)}.`,
          date: p.plannedStartDate,
        });
      }
    }

    if (p.status !== "in_progress") continue;

    let phaseCritique = false;
    phases.forEach((ph, i) => {
      if (ph.status === "completed") return;
      const phaseBase = { ...base, date: ph.plannedEnd ?? ph.plannedStart };

      if (ph.status === "blocked") {
        phaseCritique = true;
        alertes.push({
          ...phaseBase,
          key: `phase_blocked:${ph.id}`,
          kind: "phase_blocked",
          severity: "critical",
          title: `« ${nom(ph)} » est bloquée`,
          detail: phrase(
            `Rien n'avance sur cette phase, à ${pourcent(ph.progressPercent)}` +
              (ph.plannedEnd ? ` · fin prévue le ${dateCourte(ph.plannedEnd)}` : ""),
          ),
        });
        return;
      }

      if (ph.plannedEnd && ph.plannedEnd < today) {
        phaseCritique = true;
        alertes.push({
          ...phaseBase,
          key: `phase_overdue:${ph.id}`,
          kind: "phase_overdue",
          severity: "critical",
          title: `« ${nom(ph)} » est en retard`,
          detail: `Devait finir le ${dateCourte(ph.plannedEnd)} (${-daysUntil(ph.plannedEnd, today)} j de retard) · à ${pourcent(ph.progressPercent)}.`,
        });
        return;
      }

      if (ph.plannedEnd) {
        const jours = daysUntil(ph.plannedEnd, today);
        if (jours <= PHASE_DUE_SOON_DAYS) {
          // Ce qui attend derrière : c'est lui qui transforme une phase qui
          // traîne en chantier qui glisse, et c'est ce que le patron doit
          // pouvoir annoncer à l'équipe suivante.
          const suivante = phases
            .slice(i + 1)
            .find((s) => s.status !== "completed" && s.plannedStart != null);
          alertes.push({
            ...phaseBase,
            key: `phase_due_soon:${ph.id}`,
            kind: "phase_due_soon",
            severity: "warning",
            title: `« ${nom(ph)} » doit finir ${echeance(jours)}`,
            detail: phrase(
              `À ${pourcent(ph.progressPercent)}. Si elle ne l'est pas, le chantier prend du retard` +
                (suivante
                  ? ` : « ${nom(suivante)} » démarre le ${dateCourte(suivante.plannedStart!)}`
                  : ""),
            ),
          });
          return;
        }
      }

      if (
        ph.plannedStart &&
        ph.plannedStart < today &&
        ph.status === "pending" &&
        ph.progressPercent === 0
      ) {
        alertes.push({
          ...base,
          key: `phase_not_started:${ph.id}`,
          kind: "phase_not_started",
          severity: "warning",
          title: `« ${nom(ph)} » n'a pas démarré`,
          detail: `Début prévu le ${dateCourte(ph.plannedStart)}, il y a ${-daysUntil(ph.plannedStart, today)} j.`,
          date: ph.plannedStart,
        });
      }
    });

    // Le constat général se tait quand une phase dit déjà précisément ce qui
    // cloche : « en retard sur le calendrier » sous « Plâtrerie en retard de
    // 3 jours » n'apprend rien au patron et lui fait lire deux lignes.
    const ecart = scheduleGap(p, today);
    if (!finDepassee && !phaseCritique && ecart && ecart.gap >= SCHEDULE_TOLERANCE_POINTS) {
      alertes.push({
        ...base,
        key: `behind_schedule:${p.id}`,
        kind: "behind_schedule",
        severity: "warning",
        title: "Avance moins vite que le calendrier",
        detail: `${pourcent(ecart.elapsed)} du délai écoulé, ${pourcent(ecart.progress)} des travaux faits.`,
        date: p.plannedEndDate,
      });
    }
  }

  const ouverts = new Map(projects.filter((p) => OUVERTS.has(p.status)).map((p) => [p.id, p]));
  for (const o of orders) {
    if (!A_COMMANDER.has(o.status) || !o.neededBy || !o.projectId) continue;
    const chantier = ouverts.get(o.projectId);
    if (!chantier) continue;
    const jours = daysUntil(o.neededBy, today);
    if (jours > ORDER_LEAD_DAYS) continue;
    alertes.push({
      key: `order_needed:${o.id}`,
      kind: "order_needed",
      severity: jours < 0 ? "critical" : "warning",
      projectId: chantier.id,
      projectName: chantier.name,
      title: "Matériel à commander",
      detail:
        jours < 0
          ? `Attendu depuis le ${dateCourte(o.neededBy)}, et pas encore commandé.`
          : `Attendu le ${dateCourte(o.neededBy)}, ${echeance(jours)}, et pas encore commandé.`,
      href: "/commandes",
      date: o.neededBy,
    });
  }

  return alertes.sort(
    (a, b) =>
      RANG[a.severity] - RANG[b.severity] ||
      compareIso(a.date, b.date) ||
      a.projectName.localeCompare(b.projectName, "fr"),
  );
}

/**
 * Le délai consommé contre les travaux faits, en points.
 *
 * `null` quand on ne peut pas le dire honnêtement : sans dates de début et de
 * fin, sans phase saisie, ou hors de la fenêtre du chantier — avant le début
 * rien n'est dû, après la fin c'est `end_overdue` qui parle.
 */
export function scheduleGap(
  project: Pick<WatchedProject, "plannedStartDate" | "plannedEndDate" | "health">,
  today: string,
): { elapsed: number; progress: number; gap: number } | null {
  const { plannedStartDate: debut, plannedEndDate: fin } = project;
  const progress = project.health.progressPercent;
  if (!debut || !fin || progress == null) return null;
  const duree = daysUntil(fin, debut);
  if (duree <= 0 || today < debut || today > fin) return null;
  const elapsed = Math.round((daysUntil(today, debut) / duree) * 100);
  return { elapsed, progress, gap: elapsed - progress };
}

/**
 * Les chantiers en cours dont on ne peut rien surveiller : ni date de fin, ni
 * phase datée. L'écran les annonce, pour qu'un panneau vide ne se lise pas
 * « tout va bien ».
 */
export function unwatchedProjects(projects: readonly WatchedProject[]): number {
  return projects.filter(
    (p) =>
      p.status === "in_progress" &&
      !p.plannedEndDate &&
      // Le prédicat suit exactement ce que `siteAlerts` sait dire sans date :
      // une phase bloquée sort une alerte critique, une phase datée au début
      // sort « n'a pas démarré ». Les compter ici faisait annoncer « rien à
      // surveiller » sur un chantier dont l'écran affichait au même moment
      // une alerte critique — le compteur disait l'inverse du panneau.
      !p.phases.some(
        (ph) => ph.plannedEnd != null || ph.plannedStart != null || ph.status === "blocked",
      ),
  ).length;
}
